import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NoteModal from './NoteModal';
import PDFViewer from '../../Shared/PDFViewer';
import AdminSearchTable from '../../Admin/AdminSearchTable';

const PendingMygaSection = ({ model, agtCode, status, polNo, ownerName }) => {
    const navigate = useNavigate();

    // ====== Data + Filters ======
    const records = model?.pendingBusinessSPDA || [];

    const normalizedAgtCode = agtCode?.trim()?.toLowerCase();
    const normalizedStatus = status?.trim()?.toLowerCase();
    const normalizedPolNo = polNo?.trim()?.toLowerCase();
    const normalizedOwnerName = ownerName?.trim()?.toLowerCase();

    const getPolicyNumber = (row) => row.policyNumber ?? row.polNo ?? '';

    const filteredRecords = useMemo(() => {
        return records
            .filter(r => {
                if (!normalizedAgtCode || normalizedAgtCode === 'all') return true;
                const code = String(r?.agentCode ?? '').trim().toLowerCase();
                return code === normalizedAgtCode;
            })
            .filter(r => {
                if (!normalizedStatus || normalizedStatus === 'all') return true;
                const s = String(r?.status ?? '').trim().toLowerCase();
                return s.includes(normalizedStatus);
            })
            .filter(r => {
                if (!normalizedPolNo) return true;
                const p = getPolicyNumber(r).trim().toLowerCase();
                return p.includes(normalizedPolNo);
            })
            .filter(r => {
                if (!normalizedOwnerName) return true;
                const owner = String(r?.ownerDisplayName ?? '').trim().toLowerCase();
                return owner.includes(normalizedOwnerName);
            });
    }, [records, normalizedAgtCode, normalizedStatus, normalizedPolNo, normalizedOwnerName]);

    const hasData = filteredRecords.length > 0;

    // Normalize data for the table (so we always have a consistent policyNumberDisplay)
    const tableData = useMemo(
        () =>
            filteredRecords.map(r => ({
                ...r,
                policyNumberDisplay: getPolicyNumber(r),
            })),
        [filteredRecords]
    );

    // ====== Notes Modal (external) ======
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [noteData, setNoteData] = useState([]);

    const closeNoteModal = () => {
        setIsNoteModalOpen(false);
        setNoteData([]);
    };

    // ENV Variables
    const apiURL = import.meta.env.VITE_API;
    const logout = window.logout ?? (() => { });

    const getNotes = async (policyNumber) => {
        try {
            const res = await fetch(
                `${apiURL}/agent/getpolicynotes?polNo=${encodeURIComponent(policyNumber)}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error('unauthorized');
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error('forbidden');
            }

            if (!res.ok) {
                throw new Error('Failed to fetch notes');
            }

            return await res.json();
        } catch (err) {
            console.error(err);
            alert('Could not retrieve notes');
            return [];
        }
    };

    const handleOpenNotes = async (row) => {
        const policyNumber = getPolicyNumber(row);
        if (!policyNumber) return;

        const notes = await getNotes(policyNumber);
        setNoteData(Array.isArray(notes) ? notes : []);
        setIsNoteModalOpen(true);
    };

    // ====== Application PDF Modal (using PDFViewer) ======
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfFileId, setPdfFileId] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');

    const closePdfModal = () => {
        if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(pdfBlobUrl);
        }
        setShowPdfModal(false);
        setPdfBlobUrl(null);
        setPdfFileId(null);
        setPdfFileName('');
    };

    const getApplication = async (policyNumber) => {
        try {
            const res = await fetch(
                `${apiURL}/image/pdf/getPolicyApp?polNo=${encodeURIComponent(policyNumber)}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error('unauthorized');
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error('forbidden');
            }

            if (!res.ok) {
                throw new Error('Failed to fetch Application PDF');
            }

            const documentId = res.headers.get('x-document-id');
            const blob = await res.blob();

            return { blob, documentId };
        } catch (err) {
            console.error(err);
            alert('Could not retrieve application file');
            return null;
        }
    };

    const handleOpenApplication = async (row) => {
        const policyNumber = getPolicyNumber(row);
        if (!policyNumber) return;

        const result = await getApplication(policyNumber);
        if (!result) return;

        const { blob, documentId } = result;
        const url = URL.createObjectURL(blob);

        setPdfBlobUrl(url);
        setPdfFileId(documentId);
        setPdfFileName(`Application - ${policyNumber}`);
        setShowPdfModal(true);
    };

    // ====== Columns for AdminSearchTable ======
    const columns = [
        {
            label: 'Agent Code',
            accessor: 'agentCode',
            sortType: 'string',
        },
        {
            label: 'Agent Name',
            accessor: 'agentDisplayName',
            sortType: 'string',
        },
        {
            label: 'Policy Number',
            accessor: 'policyNumberDisplay',
            sortType: 'string',
        },
        {
            label: 'Status',
            accessor: 'status',
            sortType: 'string',
            Cell: row => row.clientStatus ?? row.status,
        },
        {
            label: 'Tax Status',
            accessor: 'qualType',
            sortType: 'string',
        },
        {
            label: 'Owner',
            accessor: 'ownerDisplayName',
            sortType: 'string',
        },
        {
            label: 'Application Premium',
            accessor: 'initialPremium',
            sortType: 'currency',
        },
        {
            label: 'Application Date',
            accessor: 'appDate',
            sortType: 'date',
        },
        {
            label: 'Received Date',
            accessor: 'recDate',
            sortType: 'date',
        },
        {
            label: 'Term',
            accessor: 'termPeriod',
            sortType: 'number',
        },
        {
            label: 'Notes',
            accessor: 'notesAction',
            sortable: false,
            Cell: row => (
                <span
                    className="paginated-table-row-clickable"
                    onClick={() => handleOpenNotes(row)}
                >
                    Notes
                </span>
            ),
        },
        {
            label: 'Application',
            accessor: 'viewAction',
            sortable: false,
            Cell: row => (
                <span
                    className="paginated-table-row-clickable"
                    onClick={() => handleOpenApplication(row)}
                >
                    View
                </span>
            ),
        },
    ];

    // ====== Render ======
    return (
        <div className="container container-section" id="claims-history-container">
            {!hasData ? (
                <div className="card notfoundbox rounded-3 shadow-lg">
                    <div className="card-body">
                        <div className="flex-container">
                            <h5>No Policies Found</h5>
                        </div>
                    </div>
                </div>
            ) : (
                <AdminSearchTable
                    columns={columns}
                    data={tableData}
                    rowsPerPage={10}
                />
            )}

            {/* External Note Modal */}
            <NoteModal
                isOpen={isNoteModalOpen}
                onClose={closeNoteModal}
                notes={noteData}
            />

            {/* PDF Modal using PDFViewer */}
            {showPdfModal && pdfBlobUrl && (
                <div className="pdf-modal-overlay">
                    <button className="pdf-modal-close" onClick={closePdfModal}>
                        x Close
                    </button>
                    <div className="pdf-modal-content">
                        <PDFViewer
                            src={pdfBlobUrl}
                            fileId={pdfFileId}
                            fileName={pdfFileName}
                            serviceForm={false}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingMygaSection;
